import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import {
  User,
  UserUpdate,
  Flashcard,
  FlashcardCreate,
  FlashcardUpdate,
  Deck,
  DeckCreate,
  DeckUpdate,
  ReviewRequest,
  ReviewResponse,
  StatisticsResponse,
  ApiResponse,
  LearningQuality,
  LearningRatingRequest,
  LearningRatingResponse,
  UnreviewedCardsResponse,
  OidcConfig,
  BCCWJMemoryStatus,
  BCCWJWord,
  BCCWJWordSearchResponse,
  BCCWJWordStatusResponse,
  BCCWJStats,
  TriageWordResponse,
} from '../types'

class ApiClient {
  private instance: AxiosInstance

  constructor(baseURL: string = import.meta.env.VITE_API_URL || 'http://localhost:8000') {
    this.instance = axios.create({
      baseURL,
      timeout: 10000,
      withCredentials: true, // send/receive httpOnly session cookies (BFF auth)
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Response interceptor: on 401, attempt one silent session refresh via the
    // backend before giving up (the backend rotates/renews the session cookie).
    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config
        const isRefreshCall =
          typeof originalRequest?.url === 'string' &&
          originalRequest.url.includes('/auth/session/refresh/')

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !isRefreshCall
        ) {
          originalRequest._retry = true
          try {
            await this.refreshSession()
            return this.instance(originalRequest)
          } catch (refreshError) {
            if (typeof window !== 'undefined') {
              const message =
                refreshError instanceof Error
                  ? refreshError.message
                  : 'Session expired. Please sign in again.'
              window.dispatchEvent(new CustomEvent('auth:expired', { detail: message }))
            }
            return Promise.reject(refreshError)
          }
        }

        return Promise.reject(error)
      }
    )
  }

  /** Silently refresh the session cookie via the backend. */
  async refreshSession(): Promise<{ ok: boolean; user: User }> {
    return this.post<{ ok: boolean; user: User }>('/auth/session/refresh/')
  }

  // Auth methods
  async getOidcConfig(): Promise<OidcConfig> {
    return this.get<OidcConfig>('/auth/config/')
  }

  /** Exchange an OIDC authorization code for a Mana session (sets cookies).

  The backend verifies the ID token, upserts the user, and returns { user };
  the session is carried by the httpOnly cookie, not the response body. */
  async exchangeAuthorizationCode(
    code: string,
    redirectUri: string,
    codeVerifier?: string,
  ): Promise<{ user: User }> {
    return this.post<{ user: User }>('/auth/token/exchange/', {
      code,
      redirect_uri: redirectUri,
      code_verifier: codeVerifier,
    })
  }

  /** Validate the current token and return user info. */
  async verifyToken(): Promise<{ valid: boolean; username: string; user_id: string }> {
    return this.get<{ valid: boolean; username: string; user_id: string }>('/auth/verify/')
  }

  async logout(): Promise<{ message: string }> {
    return this.post<{ message: string }>('/auth/logout/')
  }

  // User methods
  async getCurrentUser(): Promise<User> {
    return this.get<User>('/auth/me/')
  }

  async updateUser(userUpdates: UserUpdate): Promise<ApiResponse<User>> {
    return this.instance.put('/auth/me/', userUpdates)
  }

  // Flashcard methods
  async getFlashcards(
    deckId?: string,
    difficulty?: string,
    cardType?: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<ApiResponse<Flashcard[]>> {
    const params: any = { limit, offset }
    if (deckId) params.deck_id = deckId
    if (difficulty) params.difficulty = difficulty
    if (cardType) params.card_type = cardType
    return this.instance.get('/cards/', { params })
  }

  async getFlashcardById(id: string): Promise<ApiResponse<Flashcard>> {
    return this.instance.get(`/cards/${id}`)
  }

  async createFlashcard(flashcard: FlashcardCreate): Promise<ApiResponse<Flashcard>> {
    return this.instance.post('/cards/', flashcard)
  }

  async updateFlashcard(id: string, flashcard: FlashcardUpdate): Promise<ApiResponse<Flashcard>> {
    return this.instance.put(`/cards/${id}`, flashcard)
  }

  async deleteFlashcard(id: string): Promise<ApiResponse> {
    return this.instance.delete(`/cards/${id}`)
  }

  async searchFlashcards(query: string, deckId?: string, limit: number = 50): Promise<ApiResponse<Flashcard[]>> {
    const params: any = { q: query, limit }
    if (deckId) params.deck_id = deckId
    return this.instance.get('/cards/search/', { params })
  }

  async getFlashcardsByDeck(deckId: string, limit: number = 50, offset: number = 0): Promise<ApiResponse<Flashcard[]>> {
    // Cards-by-deck is served by the decks router (GET /decks/{deck_id}/cards/),
    // not a /cards/deck/{deck_id}/ route.
    return this.instance.get(`/decks/${deckId}/cards/`, { params: { limit, offset } })
  }

  // Deck methods
  async getDecks(
    category?: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<ApiResponse<Deck[]>> {
    // Decks are scoped to the authenticated user on the backend
    const params: any = { limit, offset }
    if (category) params.category = category
    return this.instance.get('/decks/', { params })
  }

  async getDeckById(id: string): Promise<ApiResponse<Deck>> {
    return this.instance.get(`/decks/${id}`)
  }

  async createDeck(deck: DeckCreate): Promise<ApiResponse<Deck>> {
    // The deck owner is derived from the auth token on the backend
    return this.instance.post('/decks/', deck)
  }

  async updateDeck(id: string, deck: DeckUpdate): Promise<ApiResponse<Deck>> {
    return this.instance.put(`/decks/${id}`, deck)
  }

  async deleteDeck(id: string): Promise<ApiResponse> {
    return this.instance.delete(`/decks/${id}`)
  }

  async addCardToDeck(deckId: string, cardId: string): Promise<ApiResponse<{ message: string }>> {
    return this.instance.post(`/decks/${deckId}/cards/${cardId}`)
  }

  async removeCardFromDeck(deckId: string, cardId: string): Promise<ApiResponse<{ message: string }>> {
    return this.instance.delete(`/decks/${deckId}/cards/${cardId}`)
  }

  async getDeckStatistics(deckId: string): Promise<ApiResponse<any>> {
    return this.instance.get(`/decks/${deckId}/statistics/`)
  }

  // Review methods
  async getReviewSession(
    deckId?: string,
    sessionType: string = 'mixed'
  ): Promise<ApiResponse<{ session_id?: string; cards: Flashcard[]; total_cards: number; due_cards: number; new_cards: number }>> {
    // The reviewing user is derived from the auth token on the backend.
    const params: any = {}
    if (deckId) params.deck_id = deckId
    if (sessionType) params.session_type = sessionType
    return this.instance.post('/reviews/session/start/', {}, { params })
  }

  async reviewCard(reviewRequest: ReviewRequest): Promise<ApiResponse<ReviewResponse>> {
    return this.instance.post('/reviews/review/', reviewRequest)
  }

  // Stats methods
  async getUserStatistics(): Promise<ApiResponse<StatisticsResponse>> {
    // Stats are scoped to the authenticated user on the backend.
    return this.instance.get('/stats/')
  }

  // ==================== Learning Session Methods ====================
  // Note: These methods return unwrapped data directly (not ApiResponse wrapper).
  // They use the this.get() and this.post() utility methods which extract response.data.

  /**
   * Get unreviewed cards for learning session.
   * Returns unwrapped data directly (not ApiResponse wrapper).
   *
   * @param tags - Optional array of tags to filter cards (e.g., ['N5', 'vocabulary'])
   * @param count - Maximum number of cards to retrieve (default: 100)
   * @returns Promise<UnreviewedCardsResponse> - Unwrapped response with cards and metadata
   */
  async getUnreviewedCards(
    tags?: string[],
    count: number = 100
  ): Promise<UnreviewedCardsResponse> {
    const params = new URLSearchParams({ count: count.toString() });
    if (tags && tags.length > 0) {
      params.append('tags', tags.join(','));
    }

    return this.get<UnreviewedCardsResponse>(
      `/learning/unreviewed?${params.toString()}`
    );
  }

  /**
   * Rate a card during learning session.
   * Returns unwrapped data directly (not ApiResponse wrapper).
   *
   * @param cardId - The ID of the card being rated
   * @param quality - Learning quality rating (0=AGAIN, 1=HARD, 2=GOOD, 3=EASY)
   * @returns Promise<LearningRatingResponse> - Unwrapped response with updated card data
   * @throws Error if quality is not in valid range (0-3)
   */
  async rateCard(
    cardId: string,
    quality: LearningQuality
  ): Promise<LearningRatingResponse> {
    // Runtime validation for quality parameter
    if (quality < 0 || quality > 3) {
      throw new Error(`Invalid quality value: ${quality}. Must be 0-3 (AGAIN=0, HARD=1, GOOD=2, EASY=3).`)
    }

    return this.post<LearningRatingResponse, LearningRatingRequest>(
      '/learning/rate',
      {
        card_id: cardId,
        quality: quality
      }
    );
  }

  // ==================== BCCWJ Methods ====================

  async searchBCCWJWords(
    search?: string,
    lemma?: string,
    pos?: string,
    hiragana?: string,
    limit: number = 20,
    offset: number = 0,
    status?: string
  ): Promise<BCCWJWordSearchResponse> {
    const params = new URLSearchParams()
    params.set('limit', limit.toString())
    params.set('offset', offset.toString())
    if (search) params.set('search', search)
    if (lemma) params.set('lemma', lemma)
    if (pos) params.set('pos', pos)
    if (hiragana) params.set('hiragana', hiragana)
    if (status) params.set('status', status)
    return this.get<BCCWJWordSearchResponse>(`/bccwj/words?${params.toString()}`)
  }

  async getCommonBCCWJWords(limit: number = 50): Promise<BCCWJWord[]> {
    return this.get<BCCWJWord[]>(`/bccwj/words/common?limit=${limit}`)
  }

  async getBCCWJWord(wordId: string): Promise<BCCWJWord> {
    return this.get<BCCWJWord>(`/bccwj/words/${wordId}`)
  }

  async getBCCWJPosTypes(): Promise<string[]> {
    return this.get<string[]>('/bccwj/pos-types')
  }

  async getBCCWJStats(): Promise<BCCWJStats> {
    return this.get<BCCWJStats>('/bccwj/stats')
  }

  async setBCCWJWordStatus(wordId: string, status: BCCWJMemoryStatus): Promise<BCCWJWordStatusResponse> {
    return this.put<BCCWJWordStatusResponse, { status: BCCWJMemoryStatus }>(
      `/bccwj/words/${wordId}/status`,
      { status }
    )
  }

  async clearBCCWJWordStatus(wordId: string): Promise<{ status: string; bccwj_word_id: string }> {
    return this.delete<{ status: string; bccwj_word_id: string }>(`/bccwj/words/${wordId}/status`)
  }

  // ==================== Triage Methods ====================

  async getTriageWords(
    rankMin: number = 1,
    rankMax: number = 1000,
    pos?: string,
    limit: number = 1,
    offset: number = 0
  ): Promise<TriageWordResponse> {
    const params = new URLSearchParams()
    params.set('rank_min', rankMin.toString())
    params.set('rank_max', rankMax.toString())
    params.set('limit', limit.toString())
    params.set('offset', offset.toString())
    if (pos) params.set('pos', pos)
    return this.get<TriageWordResponse>(`/bccwj/words/triage?${params.toString()}`)
  }

  async createCardFromBCCWJ(
    deckId: string,
    bccwjWordId: string,
    options?: { meaning?: string; tags?: string[] }
  ): Promise<{ card: Flashcard; bccwj_word: BCCWJWord; deck_id: string }> {
    return this.post('/bccwj/cards/from-bccwj', {
      deck_id: deckId,
      bccwj_word_id: bccwjWordId,
      ...options,
    })
  }

  // Utility methods for raw requests (if needed)
  async get<T = any>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.get<T>(endpoint, config)
    return response.data
  }

  async post<T = any, D = any>(endpoint: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.post<T>(endpoint, data, config)
    return response.data
  }

  async put<T = any, D = any>(endpoint: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.put<T>(endpoint, data, config)
    return response.data
  }

  async delete<T = any>(endpoint: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.delete<T>(endpoint, config)
    return response.data
  }
}

// Create singleton instance
export const apiClient = new ApiClient()